import React, { useState, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import esLocale from "@fullcalendar/core/locales/es";
import Button from "../../../Elements/Button/Button";
import { useAuthentication } from "../../../../Hooks/useAuthentication";
import { applyChecadorOffset } from "../../../../Utilities/checadorOffset";

// Colores por tipo de evento
const colorByTipo = {
    permisos: "#FFD700",
    incapacidades: "#ADD8E6",
    faltas: "#FFA07A",
    vacaciones: "#90EE90",
    checadas: "#C8A2C8"
};

function formatMinutesToHours(minutes) {
    if (minutes === null || minutes === undefined) return "-";

    const hrs = Math.floor(minutes / 60);
    const mins = minutes % 60;

    if (hrs === 0) return `${mins} min`;
    if (mins === 0) return `${hrs} h`;

    return `${hrs} h ${mins} min`;
}

// Etiquetas para la leyenda y el título del modal
const labelMap = {
    permisos: "Permiso",
    incapacidades: "Incapacidad",
    faltas: "Falta",
    vacaciones: "Vacaciones",
    checadas: "Checada"
};

// Determina el turno según rangos de checada (hasta 1h antes)
function getTurno(entradaStr, salidaStr) {
    const entrada = new Date(entradaStr);
    const salida = salidaStr ? new Date(salidaStr) : null;
    const toMin = date => date.getHours() * 60 + date.getMinutes();
    const first = toMin(entrada);
    const last = salida ? toMin(salida) : null;

    // Mixto mañana: checada 07:30–08:30
    if (first >= 7 * 60 + 30 && first < 8 * 60 + 30) {
        return "Mixto";
    }

    // Mixto tarde: checada 19:30–20:30
    if (first >= 19 * 60 + 30 && first < 20 * 60 + 30) {
        return "Mixto";
    }

    // Turno 1: checada 05:00–06:00
    if (first >= 5 * 60 && first < 6 * 60) {
        return "1";
    }

    // Turno 2: checada 13:00–14:00
    if (first >= 13 * 60 && first < 14 * 60) {
        return "2";
    }

    // Turno 3: checada 21:00–22:00
    if (first >= 21 * 60 && first < 22 * 60) {
        return "3";
    }

    // Fallback mixto: entrada antes de 14:00 y salida a las 14:00 o después
    if (salida && first < 14 * 60 && last >= 14 * 60) {
        return "Mixto";
    }

    // Fallback por hora de entrada
    if (first < 14 * 60) {
        return "1";
    }
    if (first < 22 * 60) {
        return "2";
    }

    return "3";
}

function getHorarioInicioTurno(turno, entrada) {
    const start = new Date(entrada);

    if (turno === "Mixto") {
        start.setHours(8, 30, 0, 0);
    } else if (turno === "1") {
        start.setHours(6, 0, 0, 0);
    } else if (turno === "2") {
        start.setHours(14, 0, 0, 0);
    } else {
        // turno 3
        if (entrada.getHours() < 6) {
            start.setDate(start.getDate() - 1);
        }
        start.setHours(22, 0, 0, 0);
    }

    return start;
}

// ── CAMBIO 1: recibe deviceId y omite lógica de turno para CQZ ──
function analyzeChecada(eventDatetime, eventType, deviceId) {
    const CHECADOR_SIN_TURNO = "checador_CQZ7233361670";

    if (!eventDatetime) {
        return {
            turno: "-",
            estatusAsistencia: "sin_fecha",
            minutesLate: null
        };
    }

    // CQZ no tiene lógica de turno/retardo — sus faltas se calculan por día sin registro
    if (deviceId === CHECADOR_SIN_TURNO) {
        return {
            turno: "-",
            estatusAsistencia: "no_aplica",
            minutesLate: null
        };
    }

    const entrada = new Date(eventDatetime.replace(" ", "T"));
    const turno = getTurno(eventDatetime, null);
    const horaInicio = getHorarioInicioTurno(turno, entrada);

    // Solo evaluamos asistencia/retardo/falta para entradas
    if (eventType !== "entry") {
        return {
            turno,
            estatusAsistencia: "no_aplica",
            minutesLate: null
        };
    }

    const diffMs = entrada.getTime() - horaInicio.getTime();
    const diffMinutes = Math.floor(diffMs / 60000);

    // Si llegó antes o hasta 5 min después = asistencia
    if (diffMinutes <= 5) {
        return {
            turno,
            estatusAsistencia: "asistencia",
            minutesLate: diffMinutes < 0 ? 0 : diffMinutes
        };
    }

    // 6 a 10 min = retardo
    if (diffMinutes >= 6 && diffMinutes <= 10) {
        return {
            turno,
            estatusAsistencia: "retardo",
            minutesLate: diffMinutes
        };
    }

    // 11 min o más = falta
    return {
        turno,
        estatusAsistencia: "falta",
        minutesLate: diffMinutes
    };
}

function getDeviceLocation(deviceId) {
    const locationMap = {
        checador_CQZ7233361670: "CEDIS",
        checador_JJA1241400074: "PLANTA",
    };

    return locationMap[deviceId] || "Ubicación desconocida";
}

export default function CalendarioEmpleado({
    permisos = [],
    incapacidades = [],
    faltas = [],
    vacaciones = [],
    checadas = []
}) {
    const [selected, setSelected] = useState(null);
    const navigate = useNavigate();
    const { numeroEmpleado } = useParams();
    const estatusLabelMap = {
        asistencia: "Asistencia",
        retardo: "Retardo",
        falta: "Falta",
        no_aplica: "No aplica",
        sin_fecha: "Sin fecha"
    };

    // Render personalizado de eventos: muestra turno
    const renderEventContent = ({ event }) => {
        const { tipo } = event.extendedProps;
        let title = labelMap[tipo] || event.title;

        if (tipo === "checadas") {
            title = event.title;
        }

        return (
            <div className="d-flex align-items-center">
                <span style={{ color: tipo === "checadas" ? "#7a4f7a" : "inherit" }}>
                    {title}
                </span>
            </div>
        );
    };

    // Construye los eventos para el calendario
    const events = useMemo(() => {
        const evs = [];
        function addOneDay(dateStr) {
            const d = new Date(dateStr);
            d.setDate(d.getDate() + 1);
            // devolvemos sólo la parte YYYY-MM-DD para allDay
            return d.toISOString().split("T")[0];
        }
        permisos.forEach((p, i) => {
            evs.push({
                id: `permiso-${i}`,
                title: p.motivo || labelMap.permisos,
                start: p.fecha,
                allDay: true,
                color: colorByTipo.permisos,
                textColor: "#000",               // texto en negro
                extendedProps: { ...p, tipo: "permisos", idx: i }
            });
        });

        incapacidades.forEach((inc, i) => {
            const startDate = inc.fechaInicio;
            const endDate = addOneDay(inc.fechaFin);

            evs.push({
                id: inc.id || `incapacidad-${i}`,
                title: inc.motivo || labelMap.incapacidades,
                start: startDate,
                end: endDate,       // +1 día para que pinte todo el último
                allDay: true,
                color: colorByTipo.incapacidades,
                textColor: "#000",
                extendedProps: { ...inc, tipo: "incapacidades", idx: i }
            });
        });

        faltas.forEach((f, i) => {
            evs.push({
                id: f.id || `falta-${i}`,
                title: f.motivo || labelMap.faltas,
                start: f.fecha,
                allDay: true,
                color: colorByTipo.faltas,
                textColor: "#000",               // texto en negro
                extendedProps: { ...f, tipo: "faltas", idx: i }
            });
        });

        vacaciones.forEach((v, i) => {
            const startDate = v.fechas[0];
            const endDate = addOneDay(v.fechas[v.fechas.length - 1]);

            evs.push({
                id: `vacaciones-${i}`,
                title: v.folio ? `Vacaciones ${v.folio}` : labelMap.vacaciones,
                start: startDate,
                end: endDate,       // ahora inclusivo hasta el último día
                allDay: true,
                color: colorByTipo.vacaciones,
                textColor: "#000",
                extendedProps: { ...v, tipo: "vacaciones", idx: i }
            });
        });

        checadas.forEach((c, i) => {
            const rawFechaHora = c?.parsed?.event_datetime;
            const eventLabel = c?.parsed?.event_label || "evento";
            const eventType = c?.parsed?.event_type || "unknown";
            const eventCode = c?.parsed?.event_code ?? null;

            if (!rawFechaHora) return;

            // Aplica corrección de zona horaria para dispositivos con offset
            const fechaHora = applyChecadorOffset(rawFechaHora, c.device_id);

            const startDate = fechaHora.replace(" ", "T");
            const hourLabel = fechaHora.slice(11, 16);

            const { turno, estatusAsistencia, minutesLate } = analyzeChecada(fechaHora, eventType, c.device_id);

            const estatusTexto =
                estatusAsistencia && estatusAsistencia !== "no_aplica"
                    ? ` - ${estatusAsistencia}`
                    : "";

            evs.push({
                id: `checada-${i}`,
                title: `${hourLabel} - ${eventLabel} (T${turno}${estatusTexto})`,
                start: startDate,
                allDay: false,
                color: colorByTipo.checadas,
                textColor: "#000",
                extendedProps: {
                    ...c,
                    tipo: "checadas",
                    idx: i,
                    eventLabel,
                    eventType,
                    eventCode,
                    turno,
                    estatusAsistencia,
                    minutesLate
                }
            });
        });

        // ── CAMBIO 3: faltas automáticas CQZ — fuera del forEach ──
        const CHECADOR_CQZ = "checador_CQZ7233361670";

        const diasConRegistroCQZ = new Set(
            checadas
                .filter(c => c.device_id === CHECADOR_CQZ && c?.parsed?.event_datetime)
                .map(c => c.parsed.event_datetime.slice(0, 10))
        );

        const hoy = new Date();
        for (let i = 0; i < 30; i++) {
            const d = new Date(hoy);
            d.setDate(hoy.getDate() - i);
            const fechaStr = d.toISOString().slice(0, 10);

            // Omite fines de semana
            const diaSemana = d.getDay(); // 0=domingo, 6=sábado
            if (diaSemana === 0 || diaSemana === 6) continue;

            if (!diasConRegistroCQZ.has(fechaStr)) {
                evs.push({
                    id: `falta-czq-${fechaStr}`,
                    title: `Falta (sin registro)`,
                    start: fechaStr,
                    allDay: true,
                    color: colorByTipo.faltas,
                    textColor: "#000",
                    extendedProps: {
                        tipo: "faltas",
                        motivo: "Sin registro en checador CEDIS",
                        fecha: fechaStr,
                        idx: `czq-${fechaStr}`
                    }
                });
            }
        }

        return evs;
    }, [permisos, incapacidades, faltas, vacaciones, checadas]);

    const handleClick = ({ event }) => setSelected(event.extendedProps);
    const handleEdit = () => {
        if (!selected) return;
        const keyMap = {
            permisos: "permisoIdx",
            incapacidades: "incapIdx",
            faltas: "faltaIdx",
            vacaciones: "vacacionIdx"
        };
        const sk = keyMap[selected.tipo];
        if (sk) {
            navigate(`/updateEmpleado/${numeroEmpleado}`, { state: { [sk]: selected.idx } });
        }
    };

    // obtener rol
    const { role } = useAuthentication();
    const isAdmin = role === "admin";

    return (
        <>
            <h1 className="mb-5">Calendario de Empleado Personal</h1>
            <div className="d-flex gap-3 mb-3">
                {Object.entries(colorByTipo).map(([t, c]) => (
                    <small key={t}>
                        <span
                            className="badge"
                            style={{
                                backgroundColor: c,
                                color: "#000"         //<- texto negro
                            }}
                        >
                            {labelMap[t]}
                        </span>
                    </small>
                ))}
            </div>

            <FullCalendar
                plugins={[dayGridPlugin, interactionPlugin]}
                initialView="dayGridMonth"
                locale={esLocale}
                headerToolbar={{ left: "prev,next today", center: "title", right: "" }}
                events={events}
                eventContent={renderEventContent}
                eventClick={handleClick}
                height="auto"
                dayMaxEventRows={3}
                dayMaxEvents
                moreLinkContent={args => `+${args.num} más`}
            />

            {selected && (
                <div className="modal d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
                    <div className="modal-dialog">
                        <div className="modal-content p-3">
                            <h5 className="mb-3">Detalle de {labelMap[selected.tipo]}</h5>

                            {selected.tipo === "checadas" && (
                                <div className="mb-3">
                                    <div><strong>Empleado:</strong> {selected.parsed?.employee_number}</div>
                                    <div><strong>Fecha y hora:</strong> {selected.parsed?.event_datetime ? applyChecadorOffset(selected.parsed.event_datetime, selected.device_id) : "-"}</div>
                                    <div><strong>Evento:</strong> {selected.parsed?.event_label}</div>
                                    {/* <div><strong>Tipo:</strong> {selected.parsed?.event_type}</div> */}
                                    {/*        <div><strong>Código:</strong> {selected.parsed?.event_code}</div> */}
                                    {/*     <div><strong>Modo de verificación:</strong> {selected.parsed?.verify_mode}</div> */}
                                    {/* <div><strong>Dispositivo:</strong> {selected.device_id}</div> */}
                                    <div><strong>Ubicación:</strong> {getDeviceLocation(selected.device_id)}</div>
                                    <div><strong>Turno:</strong> {selected.turno}</div>
                                    <div><strong>Estatus:</strong> {estatusLabelMap[selected.estatusAsistencia] || selected.estatusAsistencia}</div>
                                    {selected.minutesLate !== null && selected.estatusAsistencia !== "no_aplica" && (
                                        <div>
                                            <strong>Tiempo después de la hora de entrada:</strong>{" "}
                                            {formatMinutesToHours(selected.minutesLate)}
                                        </div>
                                    )}
                                </div>
                            )}

                            {selected.tipo === "permisos" && (
                                <div className="mb-3">
                                    <div><strong>Motivo:</strong> {selected.motivo}</div>
                                    <div><strong>Fecha:</strong> {selected.fecha}</div>
                                </div>
                            )}

                            {selected.tipo === "incapacidades" && (
                                <div className="mb-3">
                                    <div><strong>Motivo:</strong> {selected.motivo}</div>
                                    <div><strong>Desde:</strong> {selected.fechaInicio}</div>
                                    <div><strong>Hasta:</strong> {selected.fechaFin}</div>
                                </div>
                            )}

                            {selected.tipo === "faltas" && (
                                <div className="mb-3">
                                    <div><strong>Motivo:</strong> {selected.motivo}</div>
                                    <div><strong>Fecha:</strong> {selected.fecha}</div>
                                </div>
                            )}

                            {selected.tipo === "vacaciones" && (
                                <div className="mb-3">
                                    <div><strong>Folio:</strong> {selected.folio}</div>
                                    <div><strong>Días:</strong> {selected.dias}</div>
                                    <div><strong>Fechas:</strong> {selected.fechas.join(", ")}</div>
                                </div>
                            )}

                            <div className="d-flex justify-content-end gap-2 mt-3">
                                <Button text="Cerrar" color="red" fill action={() => setSelected(null)} />
                                {isAdmin && !["checadas"].includes(selected.tipo) && (
                                    <Button text="Editar" color="green" fill action={handleEdit} />
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}